import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Sparkles, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { useCompanyOptions, useConvertLead, usePipelines } from './api';
import { DEAL_TYPE_LABELS } from './labels';
import type { DealType, Lead } from './types';

const DEAL_TYPES = Object.keys(DEAL_TYPE_LABELS) as DealType[];

/**
 * Conversion (spec LEAD-06). One action creates the account, the person and the
 * opportunity together, so nobody has to re-key the same details three times.
 */
export function ConvertLeadDialog({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const convert = useConvertLead();
  const navigate = useNavigate();
  const { data: companies = [] } = useCompanyOptions();
  const { data: pipelines = [] } = usePipelines();
  const [error, setError] = useState<string | null>(null);

  const [existingCompanyId, setExistingCompanyId] = useState('');
  const [companyName, setCompanyName] = useState(lead.companyName ?? '');
  const [createDeal, setCreateDeal] = useState(true);
  const [dealTitle, setDealTitle] = useState('');
  const [dealValue, setDealValue] = useState(lead.budget ?? '');
  const [dealType, setDealType] = useState<DealType>('CUSTOM_DEVELOPMENT');
  const [pipelineId, setPipelineId] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');

  const submit = async () => {
    setError(null);
    try {
      const result = await convert.mutateAsync({
        id: lead.id,
        ...(existingCompanyId ? { companyId: existingCompanyId } : { companyName }),
        createDeal,
        ...(createDeal
          ? {
              dealTitle: dealTitle || undefined,
              dealValue: dealValue || undefined,
              dealType,
              pipelineId: pipelineId || undefined,
              expectedCloseDate: expectedCloseDate || undefined,
            }
          : {}),
      });
      onClose();
      navigate(result.dealId ? `/crm/deals/${result.dealId}` : `/crm/companies`);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not convert this lead.');
    }
  };

  const canSubmit = Boolean(existingCompanyId || companyName.trim().length >= 2);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Convert {lead.name}
          </DialogTitle>
          <DialogDescription>
            This creates the records below in one step and keeps the lead as history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                Company
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                Contact ({lead.name})
              </span>
              {createDeal && (
                <>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                    Deal
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Attach to an existing company</Label>
            <Select
              value={existingCompanyId}
              onChange={(event) => setExistingCompanyId(event.target.value)}
            >
              <option value="">Create a new company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </div>

          {!existingCompanyId && (
            <div className="space-y-1.5">
              <Label>New company name</Label>
              <Input
                value={companyName}
                placeholder="Taken from the lead if left as is"
                onChange={(event) => setCompanyName(event.target.value)}
              />
            </div>
          )}

          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border p-3">
            <Checkbox
              checked={createDeal}
              onCheckedChange={(checked) => setCreateDeal(checked === true)}
            />
            <span>
              <span className="block text-sm font-medium">Also create a deal</span>
              <span className="block text-xs text-muted-foreground">
                Starts in the first open stage of the pipeline you pick.
              </span>
            </span>
          </label>

          {createDeal && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Deal title</Label>
                <Input
                  value={dealTitle}
                  placeholder="Generated from the company and requirement if blank"
                  onChange={(event) => setDealTitle(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Value</Label>
                <Input
                  inputMode="decimal"
                  value={dealValue}
                  placeholder="0.00"
                  onChange={(event) => setDealValue(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Deal type</Label>
                <Select
                  value={dealType}
                  onChange={(event) => setDealType(event.target.value as DealType)}
                >
                  {DEAL_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {DEAL_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Pipeline</Label>
                <Select
                  value={pipelineId}
                  onChange={(event) => setPipelineId(event.target.value)}
                >
                  <option value="">Default pipeline</option>
                  {pipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Expected close</Label>
                <Input
                  type="date"
                  value={expectedCloseDate}
                  onChange={(event) => setExpectedCloseDate(event.target.value)}
                />
              </div>
            </div>
          )}

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || convert.isPending}>
            {convert.isPending ? 'Converting…' : 'Convert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
